import {
  CloseCircleLinear,
  MagniferZoomInLinear,
  MagniferZoomOutLinear,
  RestartSquareLinear,
} from '@assessprep/icons/solar';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';

// v3 ZoomableImageContainer threshold — an OS pinch-zoomed viewport (iPad)
// breaks pan/zoom math, so warn instead of opening the dialog.
const ZOOM_WARNING_SCALE_THRESHOLD = 1.06;

// 90°-step rotation → Tailwind rotate utilities (indexed by rotation / 90).
// 270° uses -rotate-90 to stay on the Tailwind scale (rotate-270 is off-scale).
const ROTATE_CLASS = ['rotate-0', 'rotate-90', 'rotate-180', '-rotate-90'] as const;

// Zoom floor mirrors react-zoom-pan-pinch's default; epsilon absorbs the
// library's float drift so the zoom-out button disables exactly at the floor.
const MIN_SCALE = 1;
const MIN_SCALE_EPSILON = 0.001;

export interface ZoomableImageLayerProps {
  /** Capture + stop claimed clicks so the v1 antd ZoomableImageContainer
   *  (bubble listener on window) can't double-open on surfaces where both
   *  layers are mounted (v4 take-preview under the v1 ExperienceLayout). */
  claimClicks?: boolean;
}

export interface ZoomableImageViewportProps {
  src: string | null;
  alt: string;
  /** End-of-toolbar slot (e.g. a dialog close button). */
  toolbarEnd?: ReactNode;
  imageTestId?: string;
  /** 90°-step rotate control — on by default everywhere (dev-directed
   *  2026-07-23, supersedes the v3-parity rotate-off content layer). */
  rotatable?: boolean;
}

/** Zoom toolbar + pan/zoom image viewport (no dialog chrome) — the shared
 *  engine behind ZoomImageDialog and the attachment preview's image kind.
 *  Toolbar law (all surfaces): zoom-in · zoom-out (disabled at min) · rotate —
 *  no reset button; zoom-out at minScale recenters via limitToBounds.
 *  Host must be a column flex container that gives it the full height. */
export const ZoomableImageViewport = ({
  src,
  alt,
  toolbarEnd,
  imageTestId = 'zoomable-image',
  rotatable = true,
}: ZoomableImageViewportProps) => {
  const { t } = useTranslation();
  // Rotation is direction-neutral (always clockwise 90°) — RTL-safe.
  const [rotation, setRotation] = useState(0);
  // max-h/max-w constrain the img PRE-rotation, so odd (90°/270°) turns keep the
  // unrotated bounding box and can overflow the viewport — fitScale shrinks the
  // rotated box back inside it (J22T-08 "rotate looks broken").
  const [fitScale, setFitScale] = useState(1);
  const [atMinScale, setAtMinScale] = useState(true);
  const viewportRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const motionSafeTime = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : undefined;

  const computeFitScale = (nextRotation: number) => {
    if (nextRotation % 180 === 0) return 1;
    const viewport = viewportRef.current;
    const image = imageRef.current;
    if (!viewport || !image) return 1;
    const { clientWidth: viewportWidth, clientHeight: viewportHeight } = viewport;
    const { offsetWidth: imageWidth, offsetHeight: imageHeight } = image;
    if (!viewportWidth || !viewportHeight || !imageWidth || !imageHeight) return 1;
    // Rotated 90°: visual width = layout height and vice versa.
    return Math.min(viewportHeight / imageWidth, viewportWidth / imageHeight, 1);
  };

  const rotateBy90 = () => {
    const next = (rotation + 90) % 360;
    setRotation(next);
    setFitScale(computeFitScale(next));
  };

  // Keep the odd-rotation fit fresh while the viewport resizes (dialog resize,
  // font-scale/orientation changes).
  useEffect(() => {
    if (rotation % 180 === 0) return undefined;
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setFitScale(computeFitScale(rotation)));
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [rotation]);

  // Attachment switches must not inherit the previous image's view state —
  // key remounts pan/zoom, the effect clears rotation/fit (the reset button is
  // gone, so a leak would have no one-click escape).
  useEffect(() => {
    setRotation(0);
    setFitScale(1);
    setAtMinScale(true);
  }, [src]);

  return (
    <TransformWrapper
      key={src ?? 'empty'}
      initialScale={1}
      minScale={MIN_SCALE}
      onTransformed={(_ref, state) => setAtMinScale(state.scale <= MIN_SCALE + MIN_SCALE_EPSILON)}
    >
      {({ zoomIn, zoomOut }) => (
        <>
          <div className="relative flex shrink-0 items-center justify-center gap-1 border-b border-border bg-muted p-1">
            <Button
              type="button"
              variant="ghost"
              className="size-11 [&_svg:not([class*='size-'])]:size-5"
              onClick={() => zoomIn(undefined, motionSafeTime())}
              aria-label={t('FabricTools.zoom_in')}
              data-testid="zoom-in-button"
            >
              <MagniferZoomInLinear aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="size-11 [&_svg:not([class*='size-'])]:size-5"
              onClick={() => zoomOut(undefined, motionSafeTime())}
              aria-label={t('FabricTools.zoom_out')}
              data-testid="zoom-out-button"
              disabled={atMinScale}
            >
              <MagniferZoomOutLinear aria-hidden="true" />
            </Button>
            {rotatable && (
              <Button
                type="button"
                variant="ghost"
                className="size-11 [&_svg:not([class*='size-'])]:size-5"
                onClick={rotateBy90}
                aria-label={t('FabricTools.rotate_right')}
                data-testid="zoom-rotate-button"
              >
                <RestartSquareLinear aria-hidden="true" />
              </Button>
            )}
            {toolbarEnd}
          </div>
          <div className="min-h-0 flex-1" ref={viewportRef} data-testid="zoom-viewport">
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {src && (
                <img
                  src={src}
                  alt={alt}
                  ref={imageRef}
                  // Rotation via Tailwind rotate scale (0/90/180/270°) — composes
                  // with the wrapper's zoom; 270° maps to -rotate-90 (on-scale).
                  // `scale` (measured fitScale) shrinks odd turns into the viewport.
                  // Known gap on lockdown-client Chromium (pre-104): standalone
                  // `rotate`/`scale` don't parse there, so odd turns render
                  // unrotated and unscaled (the image still fits its box, so
                  // nothing overflows). Collapsing both into one dynamic
                  // `transform` would fix it but also starts honouring
                  // `transition-transform` — an animation delta needing sign-off.
                  className={cn(
                    'max-h-full max-w-full object-contain p-2 transition-transform',
                    ROTATE_CLASS[rotation / 90]
                  )}
                  style={fitScale < 1 ? { scale: String(fitScale) } : undefined}
                  onLoad={() => {
                    if (rotation % 180 !== 0) setFitScale(computeFitScale(rotation));
                  }}
                  data-testid={imageTestId}
                />
              )}
            </TransformComponent>
          </div>
        </>
      )}
    </TransformWrapper>
  );
};

/** Controlled full-screen pan/zoom image dialog — the layer's dialog half,
 *  exported standalone for engine surfaces (MediaShow, R15b seam #6). */
export const ZoomImageDialog = ({
  src,
  onClose,
  rotatable = true,
}: {
  src: string | null;
  onClose: () => void;
  rotatable?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={!!src}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        // v3 parity: no mask close, no Esc close — the toolbar button is the only exit.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        // dvh fallback: lockdown-client Chromium (pre-108) drops `dvh`
        // declarations silently and the dialog collapses to auto height. vh is
        // the unconditional base; Tailwind emits the @supports variant after
        // the bare utility, so dvh wins wherever it parses.
        className="top-2.5 flex h-[calc(100vh-1.25rem)] w-[calc(100vw-1rem)] max-w-none translate-y-0 flex-col gap-0 overflow-hidden p-0 supports-[height:100dvh]:h-[calc(100dvh-1.25rem)] motion-reduce:animate-none sm:max-w-none"
        data-testid="zoomable-image-dialog"
      >
        <DialogTitle className="sr-only">{t('CommonText.image')}</DialogTitle>
        <ZoomableImageViewport
          src={src}
          alt={t('CommonText.image')}
          rotatable={rotatable}
          toolbarEnd={
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                className="absolute end-1 size-11 [&_svg:not([class*='size-'])]:size-5"
                aria-label={t('CommonText.close')}
                data-testid="zoom-close-button"
              >
                <CloseCircleLinear aria-hidden="true" />
              </Button>
            </DialogClose>
          }
        />
      </DialogContent>
    </Dialog>
  );
};

/** Controlled OS-pinch-zoom warning dialog (v3 ZoomWarningModal twin). */
export const ZoomWarningDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="max-w-sm motion-reduce:animate-none"
        data-testid="zoom-warning-dialog"
      >
        <DialogTitle className="text-base font-medium">
          {t('MediaShow.zoom_out_warning')}
        </DialogTitle>
        <DialogFooter>
          <Button type="button" onClick={onClose} data-testid="zoom-warning-got-it">
            {t('CommonText.got_it')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Native port of v3's global ZoomableImageContainer: a window-delegated
 *  click listener (selector contract byte-identical to v3) that opens any
 *  content image in a full-screen pan/zoom dialog. */
export const ZoomableImageLayer = ({ claimClicks = false }: ZoomableImageLayerProps) => {
  const [zoomableImageSrc, setZoomableImageSrc] = useState<string | null>(null);
  const [showZoomWarning, setShowZoomWarning] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const target = event.target;
      // v3 contract: content images only, Wiris formulas + opt-out excluded.
      if (!(target instanceof Element)) return;
      if (!target.matches('.show-html img:not(.Wirisformula)')) return;
      if (target.matches('.image-zoom-disabled img')) return;
      // J22-B04: while the image highlighter is armed it OWNS image clicks
      // (useTextHighlighter marks its scope `.image-highlighter-active`).
      // v3 won this by bubble ordering; our capture-phase claim must yield.
      if (target.closest('.image-highlighter-active')) return;
      event.preventDefault();
      if (claimClicks) event.stopPropagation();
      if (target instanceof HTMLImageElement) {
        const visualViewport = window.visualViewport;
        if (visualViewport && visualViewport.scale > ZOOM_WARNING_SCALE_THRESHOLD) {
          setShowZoomWarning(true);
          return;
        }
        if (target.src) setZoomableImageSrc(target.src);
      }
    };
    // Delegated on window so dynamically rendered images are covered (v3 parity);
    // capture phase only when claiming (runs before the v1 bubble listener).
    window.addEventListener('click', handler, claimClicks);
    return () => window.removeEventListener('click', handler, claimClicks);
  }, [claimClicks]);

  return (
    <>
      <ZoomImageDialog src={zoomableImageSrc} onClose={() => setZoomableImageSrc(null)} />
      <ZoomWarningDialog open={showZoomWarning} onClose={() => setShowZoomWarning(false)} />
    </>
  );
};
